/**
 * RoutesNextPortTab - NextPort predictions tab content for PopupRoutesPage.
 *
 * Renders the search input, prediction count, sortable table of
 * NextPort arrival predictions with probability indicators and row actions.
 */
import React from 'react';
import {
  Button,
  Card,
  SearchInput,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import { cn, formatVesselName } from '@ais-ui/utils';
import { PiDotsThreeVerticalBold } from 'react-icons/pi';
import type { EnrichedNextPortPrediction } from './ArrivalsDataProvider';
import type { DensityClasses } from './shared/popupDensityClasses';
import { SortableHead } from './RoutesTableComponents';

export interface RoutesNextPortTabProps {
  predictions: EnrichedNextPortPrediction[];
  search: string;
  onSearchChange: (value: string) => void;
  sortColumn: 'name' | 'eta' | 'probability';
  sortAscending: boolean;
  onSort: (column: string) => void;
  onViewTarget: (prediction: EnrichedNextPortPrediction) => void;
  onZoomToTarget: (prediction: EnrichedNextPortPrediction) => void;
  formatDateTime: (
    date: Date | null | undefined,
    hideSeconds?: boolean,
  ) => string;
  densityClasses: DensityClasses;
  messages: Record<string, string>;
}

export const RoutesNextPortTab: React.FC<RoutesNextPortTabProps> = ({
  predictions,
  search,
  onSearchChange,
  sortColumn,
  sortAscending,
  onSort,
  onViewTarget,
  onZoomToTarget,
  formatDateTime,
  densityClasses,
  messages,
}) => (
  <Card className="flex-1 flex flex-col overflow-hidden min-h-0">
    {/* Search input */}
    <div className="p-3 border-b flex-shrink-0 flex items-center gap-4">
      <SearchInput
        placeholder={
          messages.popupRoutesNextportSearch ||
          'Search vessels (min. 3 characters)'
        }
        className="max-w-xs h-9"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        onClear={() => onSearchChange('')}
      />
      <span className={cn('text-muted-foreground', densityClasses.text)}>
        {predictions.length}{' '}
        {messages.popupRoutesNextportPredictions || 'predictions'}
      </span>
    </div>

    <ScrollArea className="flex-1 min-h-0">
      <Table container={false}>
        <TableHeader className="sticky top-0 bg-background">
          <TableRow>
            <TableHead className={cn('w-10', densityClasses.tableHeader)}>
              #
            </TableHead>
            <SortableHead
              column="name"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableName || 'Name'}
            </SortableHead>
            <TableHead className={densityClasses.tableHeader}>
              {messages.popupRoutesTableMmsi || 'MMSI'}
            </TableHead>
            <TableHead className={densityClasses.tableHeader}>
              {messages.popupRoutesTableImo || 'IMO'}
            </TableHead>
            <SortableHead
              column="probability"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesNextportProbability || 'Probability'}
            </SortableHead>
            <SortableHead
              column="eta"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesNextportEta || 'ETA'}
            </SortableHead>
            <TableHead className={densityClasses.tableHeader}></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {predictions.map((prediction, i) => {
            const rowKey = `${prediction.imo}-${prediction.mmsi || 'unknown'}`;
            const probabilityPercent =
              prediction.probability != null
                ? (prediction.probability * 100).toFixed(1)
                : null;
            return (
              <TableRow key={rowKey} className="odd:bg-muted/50">
                <TableCell className={densityClasses.tableCell}>
                  {i + 1}
                </TableCell>
                <TableCell
                  className={cn(
                    densityClasses.tableCell,
                    'font-medium max-w-40 truncate',
                  )}
                >
                  {prediction.name ? (
                    formatVesselName(prediction.name)
                  ) : (
                    <span className="text-muted-foreground">
                      IMO: {prediction.imo}
                    </span>
                  )}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {prediction.mmsi || '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {prediction.imo}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'block w-2 h-2 rounded-full',
                        prediction.probability == null && 'bg-muted-foreground',
                        prediction.probability != null &&
                          prediction.probability >= 0.5 &&
                          'bg-green-500',
                        prediction.probability != null &&
                          prediction.probability < 0.5 &&
                          prediction.probability > 0.2 &&
                          'bg-yellow-500',
                        prediction.probability != null &&
                          prediction.probability <= 0.2 &&
                          'bg-red-500',
                      )}
                    />
                    {probabilityPercent ? `${probabilityPercent} %` : '-'}
                  </div>
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {prediction.eta
                    ? formatDateTime(new Date(prediction.eta))
                    : '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className={densityClasses.button}>
                        <PiDotsThreeVerticalBold
                          className={densityClasses.icon.medium}
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem
                        onClick={() => onZoomToTarget(prediction)}
                        disabled={!prediction.mmsi}
                      >
                        {messages.popupRoutesNextportZoomToVessel ||
                          'Zoom to vessel'}
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => onViewTarget(prediction)}
                      >
                        {messages.popupRoutesMenuViewTarget || 'View target'}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  </Card>
);
